import React, { Component } from 'react'
import { withRouter, Link } from 'react-router-dom'

import { preloadFromObject } from '../mixins/utils'

import DoujinApi from '../api/DoujinApi'
import Loader from '../components/Loader'
import AppLink from '../components/AppLink'
import ResponsiveImage from '../components/ResponsiveImage'
import Icon from '../components/utility/Icon'
import Advertisement from '../components/ads/Advertisement'

import '../scss/Reader.scss'
import { canUseDOM } from '../mixins/dom'
import MetaTags from '../components/utility/MetaTags'

let Hammer = null
if (canUseDOM) {
  Hammer = require('hammerjs')
}

class Reader extends Component {
  static async loadData(slug, opts = {}) {
    const res = await DoujinApi.pages(slug, opts)

    return {
      isLoading: false,
      data: res.data,
      meta: res.meta
    }
  }

  static async getInitialProps({ req, match, dynamic }) {
    if (dynamic) {
      return { isLoading: true, ssr: false }
    }

    const values = await this.loadData(match.params.slug, { req: req })

    return { ...values, ssr: true }
  }

  constructor(props) {
    super(props)
    this.state = {
      isLoading: props.isLoading,
      data: props.data || {},
      currentPage: null,
      nextPage: null,
      prevPage: null,
      currentIndex: null,
      meta: props.meta
    }

    this._preloadCache = []

    this.onKeydown = this.onKeydown.bind(this)
  }

  loadPages() {
    this.setState(
      {
        isLoading: true
      },
      async () => {
        const res = await Reader.loadData(this.props.match.params.slug)
        this.setState({
          isLoading: false,
          data: res.data,
          meta: res.meta
        })
      }
    )
  }

  preloadPages() {
    const { data } = this.state

    for (
      let i = this.state.currentIndex + 1;
      i <= this.state.currentIndex + 3;
      i++
    ) {
      this.preloadPage(data.pages[i])
    }

    if (this.state.currentIndex - 1 >= 0) {
      this.preloadPage(data.pages[this.state.currentIndex - 1])
    }
  }

  preloadPage(page) {
    if (!page) {
      return
    }

    if (this._preloadCache.includes(page.id)) {
      return
    }

    this._preloadCache.push(page.id)

    preloadFromObject(page, 'full')
  }

  setCurrentPage() {
    const { data } = this.state

    const page_num = this.props.match.params.page * 1

    const page = data.pages.find(p => p.page_num === page_num)
    const index = data.pages.indexOf(page)

    this.setState(
      {
        prevPage: index > 0 ? data.pages[index - 1] : null,
        currentPage: page,
        nextPage: index < data.pages.length ? data.pages[index + 1] : null,
        currentIndex: index
      },
      () => {
        this.preloadPages()

        setTimeout(() => {
          const reader = document.querySelector('.reader')
          if (reader) {
            reader.scrollIntoView()
          }
        }, 10)
      }
    )
  }

  visitPage(page, type = 'next') {
    const { data } = this.state

    if (!page) {
      if (type === 'next') {
        this.props.history.push(`/${data.series.slug}/${data.slug}/related`)
      }

      return
    }

    this.props.history.push(
      `/${data.series.slug}/${data.slug}/read/${page.page_num}`
    )
  }

  onKeydown(e) {
    switch (e.key) {
      case 'ArrowRight':
        this.visitPage(this.state.nextPage)
        break
      case 'ArrowLeft':
        this.visitPage(this.state.prevPage, 'prev')
        break
      default:
        break
    }
  }

  onWindowSwipe(direction) {
    switch (direction) {
      case Hammer.DIRECTION_LEFT:
      case 'left':
        this.visitPage(this.state.nextPage)
        break
      case Hammer.DIRECTION_RIGHT:
      case 'right':
        this.visitPage(this.state.prevPage, 'prev')
        break
      default:
        break
    }
  }

  bindSwiping() {
    const hammer = new Hammer(document.querySelector('.reader'), {
      recognizers: [[Hammer.Swipe, { direction: Hammer.DIRECTION_HORIZONTAL }]]
    })

    hammer.on('swipe', e => {
      this.onWindowSwipe(e.direction)
    })
  }

  componentDidMount() {
    if (this.props.isLoading || this.state.isLoading) {
      this.loadPages()
    }

    if (!this.props.isLoading && !this.state.isLoading) {
      this.setCurrentPage()
      this.bindSwiping()
    }

    window.addEventListener('keydown', this.onKeydown)
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.onKeydown)
  }

  componentDidUpdate(prevProps, prevState) {
    const { data, currentPage } = this.state

    if (
      (!currentPage && data.pages) ||
      prevProps.match.params.page !== this.props.match.params.page
    ) {
      this.setCurrentPage()
    }

    if (prevState.isLoading && !this.state.isLoading) {
      this.bindSwiping()
    }
  }

  render() {
    const {
      data,
      isLoading,
      currentPage,
      nextPage,
      prevPage,
      meta
    } = this.state

    if (isLoading) {
      return <Loader />
    }

    const nextPageUrl = nextPage
      ? `/${data.series.slug}/${data.slug}/read/${nextPage.page_num}`
      : `/${data.series.slug}/${data.slug}/related`
    const prevPageUrl = prevPage
      ? `/${data.series.slug}/${data.slug}/read/${prevPage.page_num}`
      : null

    const readerPaging = currentPage ? (
      <div className="reader-paging">
        {prevPageUrl && (
          <Link to={`/${data.series.slug}/${data.slug}/read/1`}>
            <Icon icon="angle-double-left" />
          </Link>
        )}
        {prevPageUrl && (
          <Link to={prevPageUrl}>
            <Icon icon="arrow-left" />
          </Link>
        )}

        <span>
          Page {currentPage.page_num} of {data.image_count}
        </span>

        <Link to={nextPageUrl}>
          <Icon icon="arrow-right" />
        </Link>
        <Link
          to={`/${data.series.slug}/${data.slug}/read/${
            data.pages[data.pages.length - 1].page_num
          }`}
        >
          <Icon icon="angle-double-right" />
        </Link>
      </div>
    ) : null

    return (
      <React.Fragment>
        <MetaTags {...meta} />
        {readerPaging}

        <section className="container">
          <div className="reader">
            {currentPage && (
              <React.Fragment>
                {prevPageUrl && (
                  <Link
                    className="paging-link prev pu-trigger"
                    to={prevPageUrl}
                  />
                )}
                <ResponsiveImage
                  key={currentPage.id}
                  image={currentPage}
                  size="full"
                />
                <Link
                  className="paging-link next pu-trigger"
                  to={nextPageUrl}
                />
              </React.Fragment>
            )}
          </div>
        </section>

        {readerPaging}

        <div className="container">
          <Advertisement size="banner" section="reader" position="bottom" />

          <h1 className="mt-2">
            Read{' '}
            <AppLink to={`/${data.series.slug}/${data.slug}`}>
              {data.title}
            </AppLink>{' '}
            from{' '}
            <AppLink to={`/parody/${data.series.slug}`}>
              {data.series.title}
            </AppLink>{' '}
            for Free
          </h1>
        </div>
      </React.Fragment>
    )
  }
}

export default withRouter(Reader)
